<template>
  <div class="print-pay-fee-container">
    <!-- 打印标题区域：根据单据类型动态展示小区名称和单据类型 -->
    <div class="text-center print-title">
      <div id="print-title-text">
        {{ printPayFeeInfo.communityName }}
        <span>{{ printTitleType }}</span>
      </div>
    </div>

    <!-- 业主及单据基础信息展示区 -->
    <div class="print-header">
      <div class="header-row">
        <div class="header-left">{{ $t('printPayFee.owner') }}：{{ printPayFeeInfo.payObjName }}</div>
        <div class="header-right receipt-number">{{ $t('printPayFee.receiptNum') }}{{ printPayFeeInfo.receiptNum }}</div>
      </div>
      <div class="header-row">
        <div class="header-left">缴费时间：{{ printPayFeeInfo.feeTime }}</div>
        <div class="header-right">打印时间：{{ printTime }}</div>
      </div>
    </div>
    
    <!-- 收费明细表格 -->
    <table class="table print-table">
      <thead>
        <tr>
          <th class="text-center" width="35">序号</th>
          <th class="text-center" width="120">收费项目</th>
          <th class="text-center" width="85">房屋/车位</th>
          <th class="text-center" width="120">费用范围</th>
          <th class="text-center" width="95">单价</th>
          <th class="text-center" width="70">面积/用量</th>
          <th class="text-center" width="165">应收/实收</th>
        </tr>
      </thead>
      <tbody>
        <!-- 循环渲染费用明细列表 -->
        <tr 
          v-for="(item, index) in printPayFeeInfo.fees" 
          :key="item.detailId || index"
        >
          <td class="text-center">{{ index + 1 }}</td>
          <td class="text-center">{{ item.feeName }}</td>
          <td class="text-center">{{ item.objName }}</td>
          <td class="text-center">
            <!-- 水电类费用：显示日期范围和起止读数 -->
            <div v-if="item.preDegrees">
              {{ dateFormat(item.startTime) }}至{{ dateFormat(item.endTime) }}<br />
              {{ item.preDegrees }}至{{ item.curDegrees }}
            </div>
            <!-- 特殊费用类型：显示"无" -->
            <div v-else-if="item.feeTypeCd === '888800010006'">
              无
            </div>
            <!-- 普通费用：仅显示日期范围 -->
            <div v-else>
              {{ dateFormat(item.startTime) }}至<br />
              {{ dateFormat(item.endTime) }}
            </div>
          </td>
          <td class="text-center price-column">{{ item.squarePrice }}{{ item.units }}</td>
          <td class="text-center">{{ item.area }}</td>
          <td class="text-center no-wrap">
            {{ item.receivableAmount }}/{{ item.amount }}元
            <span v-if="item.amount < 0"><br />(退款)</span>
          </td>
        </tr>

        <!-- 金额合计行 -->
        <tr class="total-row">
          <td colspan="2" class="text-center">大写人民币</td>
          <td colspan="3" class="text-center">{{ changeNumMoneyToChinese(printPayFeeInfo.amount) }}</td>
          <td colspan="2" class="text-center">合计：{{ printPayFeeInfo.amount }}元</td>
        </tr>

        <!-- 备注信息和客户签名栏 -->
        <tr class="print-custom-row">
          <td colspan="4">
            <div class="print-content" v-html="printPayFeeInfo.content || '温馨提示：感谢您的支持，祝您生活愉快！'"></div>
          </td>
          <td colspan="3">
            客户签名确认：
          </td>
        </tr>
      </tbody>
    </table>

    <!-- 单据联次说明 -->
    <div class="print-footer">
      <span>本单据一式三联，白单交财务，红单交物业，黄单交客户。</span>
    </div>
    
    <!-- 打印操作按钮栏：打印时自动隐藏 -->
    <div id="print-btn" class="print-button-bar">
      <el-button type="primary" class="float-right" @click="handlePrint">
        <i class="el-icon-printer"></i>&nbsp;{{ $t('common.print') }}
      </el-button>
      <el-button type="warning" class="float-right" style="margin-right: 20px" @click="handleClose">
        {{ $t('common.cancel') }}
      </el-button>
    </div>
  </div>
</template>

<script>
import { getCommunityId, getCommunityName } from '@/api/community/communityApi'
import { queryFeeReceipt, queryFeeReceiptDetail, queryFeePrintSpec } from '@/api/fee/printPayFeeApi'
import { dateFormat } from '@/utils/dateUtil'
import { changeNumMoneyToChinese } from '@/utils/moneyUtil' 

/**
 * 物业费收据打印组件
 * 
 * @description 支持单张/批量打印物业费收据，适配241-3联二等分复写纸(241mm×140mm)
 *              提供申请单、收据、退款单三种单据类型，支持自定义打印标题和备注
 *              专门针对针式打印机进行了字体、边框和尺寸优化
 * 
 * @author Proton.G
 * @version 2.0.0
 * @date 2026-05-23
 * 
 * @example 调用方式
 * // 单张打印
 * this.$router.push({ path: '/print/payFee', query: { receiptId: '123456' } })
 * // 批量打印
 * this.$router.push({ path: '/print/payFee', query: { receiptIds: '123456,789012' } })
 * // 打印申请单
 * this.$router.push({ path: '/print/payFee', query: { receiptId: '123456', apply: 'Y' } })
 */
export default {
  name: 'PrintPayFeeList',

  /**
   * 组件响应式数据
   * @property {Object} printPayFeeInfo - 打印核心数据对象
   * @property {string} printPayFeeInfo.communityName - 小区名称或自定义打印标题
   * @property {string} printPayFeeInfo.receiptId - 单张收据ID
   * @property {string} printPayFeeInfo.receiptIds - 批量打印收据ID集合(逗号分隔)
   * @property {string} printPayFeeInfo.detailIds - 费用明细ID集合(逗号分隔)
   * @property {string} printPayFeeInfo.amount - 实收总金额(保留两位小数)
   * @property {Array} printPayFeeInfo.fees - 费用明细列表
   * @property {string} printPayFeeInfo.feeTime - 缴费时间
   * @property {string} printPayFeeInfo.content - 自定义打印备注内容
   * @property {string} printPayFeeInfo.payObjName - 缴费人姓名
   * @property {Array} printPayFeeInfo.feeReceipt - 收据主表数据集合
   * @property {string} printPayFeeInfo.apply - 单据类型：Y=申请单 N=收据 R=退款单
   * @property {string} printPayFeeInfo.receiptNum - 收据编号
   * @property {string} printPayFeeInfo.acctAmount - 记账总金额(保留两位小数)
   * @property {boolean} printPayFeeInfo.merge - 是否合并相同费用项
   * @property {string} communityId - 当前登录用户所属小区ID
   * @property {string} printTime - 打印操作执行时间
   */
  data() {
    return {
      printPayFeeInfo: {
        communityName: '',
        receiptId: '',
        receiptIds: '',
        detailIds: '',
        amount: '0.00',
        fees: [],
        feeTime: '',
        content: '',
        payObjName: '',
        feeReceipt: [],
        apply: 'N',
        receiptNum: '',
        acctAmount: '0.00',
        merge: false
      },
      communityId: '',
      printTime: ''
    }
  },

  /**
   * 计算属性
   */
  computed: {
    /**
     * 根据单据类型获取对应的国际化标题文本
     * @returns {string} 单据类型标题
     */
    printTitleType() {
      const type = this.printPayFeeInfo.apply
      const typeMap = {
        Y: this.$t('printPayFee.apply'),
        N: this.$t('printPayFee.receipt'),
        R: this.$t('printPayFee.refund')
      }
      return typeMap[type] || typeMap.N
    }
  },

  /**
   * 组件创建完成生命周期钩子
   */
  created() {
    this.initBaseData()
    this.initPrintData()
  },

  methods: {
    /**
     * 初始化基础数据
     * @description 获取当前小区ID和系统当前时间
     */
    initBaseData() {
      this.communityId = getCommunityId()
      // 原生JS格式化时间（保证显示完整时分秒）
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      const seconds = String(now.getSeconds()).padStart(2, '0')
      this.printTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },

    /**
     * 初始化打印参数
     * @description 从路由参数中获取打印相关参数，并加载打印数据和配置
     */
    initPrintData() {
      const { query } = this.$route
      this.printPayFeeInfo = {
        ...this.printPayFeeInfo,
        receiptId: query.receiptId || '',
        receiptIds: query.receiptIds || '',
        detailIds: query.detailIds || '',
        apply: query.apply || 'N',
        merge: query.merge || false,
        communityName: getCommunityName()
      }

      this.loadReceiptData()
      this.loadPrintConfig()
    },

    /**
     * 日期格式化工具方法
     * @type {Function}
     * @param {Date|string|number} date - 待格式化的日期
     * @param {string} format - 格式化字符串
     * @returns {string} 格式化后的日期字符串
     */
    dateFormat,

    /**
     * 阿拉伯数字金额转中文大写工具方法
     * @type {Function}
     * @param {number|string} num - 待转换的金额数字
     * @returns {string} 中文大写金额字符串
     */
    changeNumMoneyToChinese,

    /**
     * 加载收据主表数据
     * @description 从后端获取收据主表信息，计算总金额，并加载对应的费用明细
     */
    loadReceiptData() {
      const params = {
        page: 1,
        row: 30,
        communityId: this.communityId,
        receiptId: this.printPayFeeInfo.receiptId,
        receiptIds: this.printPayFeeInfo.receiptIds,
        detailIds: this.printPayFeeInfo.detailIds
      }

      queryFeeReceipt(params)
        .then(res => {
          const feeReceipt = res.data || []
          this.printPayFeeInfo.feeReceipt = feeReceipt

          if (feeReceipt.length === 0) return

          const totalAmount = feeReceipt.reduce(
            (sum, item) => sum + parseFloat(item.amount || 0), 0
          )

          const firstReceipt = feeReceipt[0]
          this.printPayFeeInfo.amount = totalAmount.toFixed(2)
          this.printPayFeeInfo.feeTime = firstReceipt.createTime || ''
          this.printPayFeeInfo.payObjName = firstReceipt.payObjName || ''
          this.printPayFeeInfo.receiptNum = firstReceipt.receiptCode || ''

          this.loadReceiptDetail()
        })
        .catch(err => {
          console.error('加载收据主表数据失败：', err)
        })
    },

    /**
     * 加载费用明细数据
     * @description 从后端获取费用明细信息，关联房屋/车位信息，自动识别退款单
     */
    loadReceiptDetail() {
      const params = {
        page: 1,
        row: 100,
        communityId: this.communityId,
        mergeFee: this.printPayFeeInfo.merge,
        receiptId: this.printPayFeeInfo.receiptId,
        receiptIds: this.printPayFeeInfo.receiptIds,
        detailIds: this.printPayFeeInfo.detailIds
      }

      queryFeeReceiptDetail(params)
        .then(res => {
          const details = res.data || []
          if (details.length === 0) {
            this.printPayFeeInfo.fees = []
            return
          }

          if (details[0].payOrderId) {
            this.printPayFeeInfo.receiptNum += `(${details[0].payOrderId})`
          }
          
          if (details[0].amount < 0) {
            this.printPayFeeInfo.apply = 'R'
          }

          const totalAcctAmount = details.reduce((sum, item) => {
            const mainReceipt = this.printPayFeeInfo.feeReceipt.find(
              receipt => receipt.receiptId === item.receiptId
            )
            if (mainReceipt) {
              item.objName = mainReceipt.objName
              item.feeTypeCd = mainReceipt.feeTypeCd
            }
            return sum + parseFloat(item.acctAmount || 0)
          }, 0)

          this.printPayFeeInfo.fees = details
          this.printPayFeeInfo.acctAmount = totalAcctAmount.toFixed(2)
        })
        .catch(err => {
          console.error('加载费用明细数据失败：', err)
        })
    },

    /**
     * 加载打印配置信息
     * @description 从后端获取自定义打印标题和备注内容
     */
    loadPrintConfig() {
      const params = {
        page: 1,
        row: 1,
        specCd: 2020,
        communityId: this.communityId
      }

      queryFeePrintSpec(params)
        .then(res => {
          const configList = res.data || []
          if (configList.length > 0) {
            const config = configList[0]
            this.printPayFeeInfo.content = config.content || ''
            if (config.printName) {
              this.printPayFeeInfo.communityName = config.printName
            }
          }
        })
        .catch(err => {
          console.error('加载打印配置失败：', err)
        })
    },

    /**
     * 执行打印操作
     * @description 隐藏打印按钮，调用浏览器打印功能，打印完成后关闭窗口
     */
    handlePrint() {
      document.getElementById('print-btn').style.display = 'none'
      window.print()
      window.close()
    },

    /**
     * 取消打印操作
     * @description 直接关闭当前打印窗口
     */
    handleClose() {
      window.close()
    }
  }
}
</script>

<style scoped>
/* 网页预览基础样式 */
.print-pay-fee-container {
  max-width: 800px;
  margin: 20px auto;
  padding: 25px;
  font-family: "SimHei", "Microsoft YaHei", sans-serif, "SimSun", "宋体";
  color: #333;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 通用工具类 */
.text-center { text-align: center; }
.text-left { text-align: left; }
.float-right { float: right; }

/* 打印标题样式 */
.print-title {
  font-size: 24px;
  font-weight: bold;
  margin: 0 0 20px;
  letter-spacing: 2px;
  color: #000;
}

/* 头部信息区域样式 */
.print-header {
  font-size: 16px;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}
.header-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
  overflow: visible;
}
.header-left { 
  width: 40%; 
  text-align: left; 
  white-space: nowrap;
}
.header-right { 
  width: 60%; 
  text-align: right; 
  white-space: nowrap;
}
.receipt-number { white-space: nowrap; }

/* 收费明细表格样式 */
.print-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  table-layout: fixed;
  margin: 10px 0;
}
.print-table th, .print-table td {
  border: 1px solid #ccc;
  padding: 8px 4px;
  vertical-align: middle;
  word-break: break-all;
}
.print-table th {
  background: #f5f5f5;
  font-weight: bold;
}
.price-column, .no-wrap {
  white-space: nowrap;
}

/* 金额合计行样式 */
.total-row {
  font-weight: bold;
  background: #fafafa;
}

/* 备注和签名行样式 */
::v-deep .print-custom-row td {
  height: 60px;
  vertical-align: middle;
  padding-left: 8px;
  text-align: left !important;
}
.print-content {
  font-size: 13px;
  line-height: 1.4;
}

/* 页脚联次说明样式 */
.print-footer {
  margin-top: 15px;
  text-align: center;
  font-size: 14px;
  color: #666;
}

/* 打印操作按钮栏样式 */
.print-button-bar {
  margin-top: 30px;
  overflow: hidden;
}

/* 打印专用样式：适配241-3联二等分复写纸和针式打印机 */
@media print {
  /* 纸张尺寸和边距设置 */
  @page {
    size: 241mm 140mm;
    margin: 8mm 25mm 8mm 25mm;
  }

  /* 全局打印样式重置 */
  body {
    font-family: "SimHei", "Microsoft YaHei", sans-serif, "SimSun", "宋体" !important;
    font-size: 14px !important;
    color: #000 !important;
    background: #fff !important;
    box-shadow: none !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* 打印容器样式重置 */
  .print-pay-fee-container {
    margin: 0 !important;
    padding: 0 !important;
    max-width: none !important;
    box-shadow: none !important;
  }

  /* 打印标题样式优化 */
  ::v-deep #print-title-text,
  ::v-deep #print-title-text * {
    font-size: 18px !important;
    font-weight: bold !important;
    letter-spacing: 3px !important;
    text-align: center !important;
    margin-bottom: 12px !important;
  }

  /* 头部信息打印样式优化 */
  .print-header {
    margin-bottom: 5px !important;
    border: none !important;
    padding-bottom: 0 !important;
  }
  .header-row { margin-bottom: 3px !important; }

  /* 表格打印样式优化 */
  .print-table {
    width: 100% !important;
    border-collapse: collapse !important;
    border: 1px solid #000 !important;
  }
  .print-table th, .print-table td {
    border: 1px solid #000 !important;
    padding: 2px 1px !important;
    text-align: center !important;
  }
  
  /* 备注和签名行打印样式 */
  ::v-deep .print-custom-row td {
    text-align: left !important;
    vertical-align: middle !important;
    height: 15mm !important;
    padding-left: 8px !important;
  }

  /* 分页保护设置 */
  thead { display: table-header-group; }
  tr, .print-footer {
    page-break-inside: avoid !important;
  }

  /* 页脚打印样式优化 */
  .print-footer {
    margin-top: 3px !important;
    color: #000 !important;
    font-size: 14px !important;
  }

  /* 隐藏打印操作按钮 */
  #print-btn { display: none !important; }
}
</style>